/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { ModalController, ToastController } from '@ionic/angular';
import { WebBlocker } from '@shared/decorators/web-blocker.decorator';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

// Services
import { UserService } from '@shared/services/user.service';
import { UtilService } from '@shared/utils/util';

// Components
import { ProfileAlreadyValidatedComponent } from '@shared/components/profile-already-validated/profile-already-validated.component';
import { ValidateProfileComponent } from '@shared/components/validate-profile/validate-profile.component';
import { ChangeProfilePhotoComponent } from '@shared/components/change-profile-photo/change-profile-photo.component';
import { ProfilePage } from '../pages/profile/profile.page';
import { ValidationCompletedComponent } from '@shared/components/validation-completed/validation-completed.component';
import { NewPhotoValidationComponent } from '@shared/components/new-photo-validation/new-photo-validation.component';
import { NewPhotoNotApprovedComponent } from '@shared/components/new-photo-not-approved/new-photo-not-approved.component';

const USER_DATA_KEY = 'USER_DATA';
const MODAL_SHOWN_KEY = 'MODAL_SHOWN_COUNT';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
    userData: any;

    // Control the scroll behavior of the app to show pop-ups
    private blockScrollSource = new Subject<boolean>();
    public blockScroll$ = this.blockScrollSource.asObservable();

    constructor(public modalCtrl: ModalController,
                private router: Router,
                private userService: UserService,
                public util: UtilService,
                public toastCtrl: ToastController
    ) {
    }

    async getUserData(): Promise<any> {
        const val = await Preferences.get({ key: USER_DATA_KEY });
        this.userData = val.value ? JSON.parse(val.value) : null;
    
        return this.userData;
    }

    async setUserData(data: any): Promise<void> {
        this.userData = data; 
        await Preferences.set({ key: 'USER_DATA_KEY', value: JSON.stringify(data) });
    }

    async openValidateProfileComponent(): Promise<void> {
        await this.getUserData(); // Garante que os dados do usuário estão atualizados
        if (this.userData?.userValidated) {
            await this.openAlreadyValidatedPopUp();
        } else {
            await this.openValidateYourselfPopUp();
        }
    }
    async openAlreadyValidatedPopUp(): Promise<void> {
        this.blockScrollSource.next(true);
        const modal = await this.modalCtrl.create({
            component: ProfileAlreadyValidatedComponent,
            cssClass: 'custom-class-modal',
            backdropDismiss: true
        });
        await modal.present();
        await modal.onDidDismiss();
        this.blockScrollSource.next(false);

    }
    async openValidateYourselfPopUp(): Promise<void> {
        await this.getUserData();
      
        this.blockScrollSource.next(true);
        const modal = await this.modalCtrl.create({
            component: ValidateProfileComponent,
            componentProps: { me: this.userData },
            cssClass: 'custom-class-modal',
            backdropDismiss: true
        });
  
        await modal.present();
        await modal.onDidDismiss().then((val) => {
            if (val && val.data === 'openValidation') {
            this.blockScrollSource.next(false);
              this.openValidation();
            }
        });
    }

    async openChangePhotoPopUp(): Promise<void> {
        this.blockScrollSource.next(true);

        const modal = await this.modalCtrl.create({
            component: ChangeProfilePhotoComponent,
                componentProps: {
                me: this.userData
            },
            cssClass: 'custom-class-modal',
            backdropDismiss: true
            });
            modal.present();
    
            (await modal).onDidDismiss().then((val) => {
              if (val.data !== undefined && val.data !== null && val.data === 'editPhoto') {
                this.editPhotoProfile()
              }
            })
        this.blockScrollSource.next(false);
    }

    @WebBlocker()
    private navigateToHomeCheckProfile(): void {
        this.router.navigate(['/home-check-profile'], { queryParams: { originValidation: '/tabs/my-profile-display' } });
    }

    @WebBlocker()
    private navigateToInstructionsCheckProfile(): void {
        this.router.navigate(['/instructions-check-profile'], { queryParams: { originValidation: '/tabs/my-profile-display' } });
    }

    async openValidation(): Promise<void> {
        await this.getUserData();

        if (!this.userData) return;
        
        // The user is already validated
        if (this.userData?.userValidated){
            await this.openAlreadyValidatedPopUp();
            return;
        } 
        // Colect Variables
        const { hasValidPhone, hasValidPoseSelfie } = this.userData || {};
    
        // Clauses 
        const hasSkippedInitialStep = !!localStorage.getItem('skipStep');
        const needsToAdjustProfilePic = hasValidPoseSelfie;
        const needsInitialSetup = !hasValidPhone && !hasSkippedInitialStep && !hasValidPoseSelfie;

        if (needsInitialSetup){
          this.navigateToHomeCheckProfile();
          return;
        }

        // The user has a valid selfie but is not validated yet
        if (needsToAdjustProfilePic) {
          this.validateProfile(true);
          return;
        }
        
        if (!hasValidPoseSelfie){
          this.validateProfile(false);
          this.navigateToInstructionsCheckProfile();
        }

    }

    async openValidationCompletedPopUp(): Promise<void> {
        this.blockScrollSource.next(true);
    
        const modal = await this.modalCtrl.create({
          component: ValidationCompletedComponent,
          componentProps: {
            me: this.userData
          },
          cssClass: 'custom-class-modal',
          backdropDismiss: true
        });
        modal.present();

        modal.onDidDismiss().then(async (_val) => {
          this.setUserData(this.userData);
        });

        await modal.onDidDismiss();
        this.blockScrollSource.next(false);
    }

    async openNewPhotoValidationPopUp(): Promise<void>{
        this.blockScrollSource.next(true);
        const modal = await this.modalCtrl.create({
          component: NewPhotoValidationComponent,
          componentProps: {
            me: this.userData
          },
          cssClass: 'custom-class-modal',
          backdropDismiss: true
        });

        (await modal).present();
        await modal.onDidDismiss().then((_val) => {
          this.setUserData(this.userData);
        });
        await modal.onDidDismiss();
        
        this.blockScrollSource.next(false);
    }

    async openNewPhotoNotApprovedPopUp(): Promise<any> {
        this.blockScrollSource.next(true);

        const modal = await this.modalCtrl.create({
          component: NewPhotoNotApprovedComponent,
          componentProps: {
            me: this.userData
          },
          cssClass: 'custom-class-modal',
          backdropDismiss: true
        });

        await modal.present();
        const { data } = await modal.onDidDismiss();

        this.blockScrollSource.next(false);
        return data;
    }

    async validateProfile(verifyIsCompleted: boolean): Promise<void> {
      await this.getUserData();

      // Verificar se há foto no index 0 antes de validar
      const hasPhotoAtIndex0 = this.userData?.picture0 != null;
      if (!hasPhotoAtIndex0) {
        console.log('Não há foto no index 0, não é possível validar');
        this.userData.userValidated = false;
        await this.setUserData(this.userData);
        return;
      }

      const theObs = this.userService.validateProfile(this.userData);

        theObs.subscribe(async response => {
            const data = JSON.parse(response);
            if(data.result ===  'notok' && verifyIsCompleted){
                this.openChangePhotoPopUp();
                return; 
            } else if(data.result === 'ok')  {
                this.userData.userValidated = true;
            }else{
                this.userData.userValidated = false;
            }
            await this.setUserData(this.userData);
        },
        err => {
            this.util.showToastUnableToConnect(this.toastCtrl);
            console.log('Error: ' + JSON.stringify(err));
        });
    }

  async editPhotoProfile() {
    // In case the user is already in the ProfilePage
    const topModal = await this.modalCtrl.getTop();

    if (topModal && topModal.component === ProfilePage) {
      console.log('O modal ProfilePage já está aberto. Nenhuma ação será tomada.');
      return; 
    }

    const modal = await this.modalCtrl.create({
      component: ProfilePage,
      cssClass: 'confirm-password-modal',
    });

    await modal.present();
    await modal.onDidDismiss();
    await this.getUserData;
    if(localStorage.getItem('profileApproved') === 'profileApproved') {
      this.validateProfile(true)
    }          
  }

  /**
   * Quantas vezes o aviso de revalidacao ja apareceu NESTE episodio.
   *
   * O contador era de VIDA: tres aparicoes e nunca mais, para sempre. Isso foi calibrado para
   * um evento raro — perder o selo era incomum. Desde que o salvar passou a enviar a leva
   * inteira, trocar a foto principal virou rotina, e com ela perder o selo: o orcamento de tres
   * queima numa tarde e um usuario que perca o selo meses depois nao recebe aviso nenhum.
   *
   * Agora o contador e gravado junto com o `$oid` da foto principal a que ele se refere. Foto
   * principal diferente = episodio diferente = contagem zerada. O teto de tres continua valendo
   * DENTRO de cada episodio, que e o que ele sempre quis dizer.
   *
   * Valor antigo (um numero solto, sem `|`) conta para o episodio atual, para nao reabrir o
   * aviso de quem ja gastou as tres aparicoes nesta mesma foto.
   */
  private async lerContagemDoAviso(oidDaPrincipal: string): Promise<number> {
    // Sem foto principal nao ha como identificar o episodio. Tratar o vazio como identidade
    // juntava TODOS os periodos sem `picture0` num episodio unico e vitalicio — a comparacao
    // `'' !== ''` e falsa, entao o contador nunca zerava.
    if (!oidDaPrincipal) {
      return 0;
    }

    const salvo = (await Preferences.get({ key: MODAL_SHOWN_KEY })).value ?? '';
    const [vezes, episodio] = salvo.split('|');
    const contagem = parseInt(vezes, 10) || 0;

    if (episodio === undefined) {
      // Valor do formato antigo (numero solto). Vale para o episodio atual, E E MIGRADO AQUI:
      // sem reescrever, um "3" legado seria estado absorvente — a gravacao so acontece no ramo
      // que MOSTRA o aviso, e com 3 esse ramo nunca roda, entao o valor sem `|` ficaria para
      // sempre e nenhum episodio futuro reabriria o aviso.
      await Preferences.set({ key: MODAL_SHOWN_KEY, value: contagem + '|' + oidDaPrincipal });
      return contagem;
    }

    return episodio === oidDaPrincipal ? contagem : 0;
  }

  async checkNeedsPhotoValidation() {
    if (this.userData?.hasValidPoseSelfie && !this.userData?.userValidated) {
      const oidDaPrincipal = this.userData?.picture0?.$oid ?? '';
      const modalShownCount = await this.lerContagemDoAviso(oidDaPrincipal);

      if (modalShownCount < 3) {
        console.log(`Showing modal. Current count: ${modalShownCount}`);
        this.validateProfile(true);
        const newCount = modalShownCount + 1;
        await Preferences.set({ key: MODAL_SHOWN_KEY, value: newCount + '|' + oidDaPrincipal });
      } else {
        console.log('Modal will not be shown again.');
      }
    }
  }

  public checkUserValidated() {
    return this.userData?.userValidated ? true : false;
  }

  public checkHasValidPoseSelfie() {
    return this.userData?.hasValidPoseSelfie ? true : false;
  }
}